import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';

/**
 * Margen bruto: qué deja plata y qué no. Ranking por ganancia (venta - costo
 * snapshot del remito), los vendidos bajo costo, y la venta "ciega" (productos
 * sin costo cargado) para que el margen mostrado nunca mienta.
 */

const money = (n: number | null) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}k`;
  return `$${Math.round(n).toLocaleString('es-AR')}`;
};

const margenColor = (pct: number | null) =>
  pct == null ? 'text-muted-foreground'
    : pct < 0 ? 'text-red-600 font-semibold'
    : pct < 10 ? 'text-red-600'
    : pct < 20 ? 'text-amber-600'
    : 'text-green-600';

type Orden = 'ganancia' | 'venta' | 'margenPct' | 'unidades';

export default function MargenSection({ startDate, endDate }: { startDate: string; endDate: string }) {
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tipo, setTipo] = useState<'todos' | 'productos' | 'servicios'>('todos');
  const [orden, setOrden] = useState<Orden>('ganancia');
  const [verTodo, setVerTodo] = useState(false);

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) return;
    setError('');
    setLoading(true);
    axios.get('/api/reports/margen', { params: { startDate, endDate, tipo } })
      .then((r) => setD(r.data))
      .catch((e) => setError(e.response?.data?.error || 'Error cargando datos'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, tipo]);

  const ranking = useMemo(() => {
    if (!d) return [];
    const lista = [...d.ranking].sort((a: any, b: any) => {
      if (orden === 'margenPct') return (b.margenPct ?? -999) - (a.margenPct ?? -999);
      return Number(b[orden]) - Number(a[orden]);
    });
    return verTodo ? lista : lista.slice(0, 25);
  }, [d, orden, verTodo]);

  const bajoCosto = useMemo(
    () => (d ? d.ranking.filter((r: any) => r.ganancia < 0) : []),
    [d]
  );

  if (loading && !d) return <div className="py-10 text-center text-muted-foreground">Cargando margen...</div>;
  if (error) return <div className="py-10 text-center text-red-600">{error}</div>;
  if (!d) return null;

  const th = (key: Orden, label: string) => (
    <th
      className={`pb-1.5 font-normal text-right cursor-pointer select-none ${orden === key ? 'text-foreground font-medium' : ''}`}
      onClick={() => setOrden(key)}
    >
      {label}{orden === key ? ' ↓' : ''}
    </th>
  );

  return (
    <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
      {/* Filtro de tipo */}
      <div className="flex gap-2 text-xs">
        {(['todos', 'productos', 'servicios'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTipo(t)}
            className={`px-2.5 py-1 rounded border capitalize ${tipo === t ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Venta (costo conocido)</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.ventaConCosto)}</p>
          <span className="text-xs text-muted-foreground">de {money(d.kpis.venta)} totales</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Ganancia bruta</p>
          <p className="text-2xl font-semibold leading-none mb-1.5">{money(d.kpis.ganancia)}</p>
          <span className="text-xs text-muted-foreground">venta menos costo de lo vendido</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Margen bruto</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${margenColor(d.kpis.margenPct)}`}>
            {d.kpis.margenPct != null ? `${d.kpis.margenPct}%` : '—'}
          </p>
          <span className="text-xs text-muted-foreground">sobre venta con costo conocido</span>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-1">Venta sin costo cargado</p>
          <p className={`text-2xl font-semibold leading-none mb-1.5 ${d.kpis.sinCosto.venta > 0 ? 'text-amber-600' : ''}`}>
            {money(d.kpis.sinCosto.venta)}
          </p>
          <span className="text-xs text-muted-foreground">
            {d.kpis.sinCosto.items > 0 ? `${d.kpis.sinCosto.items} producto(s) sin costo: su margen no se puede medir` : 'todo lo vendido tiene costo'}
          </span>
        </CardContent></Card>
      </div>

      {/* Vendidos bajo costo: la alarma */}
      {bajoCosto.length > 0 && (
        <Card className="border-red-500/40">
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1 text-red-600">Vendidos por debajo del costo</h3>
            <p className="text-xs text-muted-foreground mb-3">
              El precio de venta no cubrió el costo cargado. Revisar precio o costo.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {bajoCosto.map((r: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-2 truncate max-w-[280px]">{r.name}</td>
                      <td className="py-1.5 pr-2 text-right text-muted-foreground">{r.unidades} u</td>
                      <td className="py-1.5 pr-2 text-right">{money(r.venta)}</td>
                      <td className="py-1.5 text-right text-red-600 font-medium">{money(r.ganancia)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranking por ganancia */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <h3 className="font-semibold text-sm">Ranking del período</h3>
            <span className="text-xs text-muted-foreground">tocá una columna para ordenar</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Solo items con costo conocido (servicios, y productos con costo cargado).
          </p>
          {ranking.length === 0 && <p className="text-sm text-muted-foreground">Sin ventas con costo en el período</p>}
          {ranking.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="pb-1.5 font-normal">Item</th>
                    {th('unidades', 'Cant.')}
                    {th('venta', 'Venta')}
                    <th className="pb-1.5 font-normal text-right cq-hide-narrow">Costo</th>
                    {th('ganancia', 'Ganancia')}
                    {th('margenPct', 'Margen')}
                  </tr>
                </thead>
                <tbody>
                  {ranking.map((r: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-2 max-w-[280px]">
                        <span className="truncate block">{r.name}</span>
                        {r.esServicio && <span className="text-[10px] text-muted-foreground">servicio</span>}
                      </td>
                      <td className="py-1.5 pr-2 text-right text-muted-foreground">{r.unidades}</td>
                      <td className="py-1.5 pr-2 text-right">{money(r.venta)}</td>
                      <td className="py-1.5 pr-2 text-right text-muted-foreground cq-hide-narrow">{money(r.costo)}</td>
                      <td className={`py-1.5 pr-2 text-right font-medium ${r.ganancia < 0 ? 'text-red-600' : ''}`}>{money(r.ganancia)}</td>
                      <td className={`py-1.5 text-right ${margenColor(r.margenPct)}`}>
                        {r.margenPct != null ? `${r.margenPct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {d.ranking.length > 25 && !verTodo && (
            <button onClick={() => setVerTodo(true)} className="mt-2 text-xs text-muted-foreground hover:text-foreground">
              Ver los {d.ranking.length - 25} restantes
            </button>
          )}
        </CardContent>
      </Card>

      {/* Productos sin costo: la tarea pendiente */}
      {d.sinCosto.length > 0 && (
        <Card className="border-amber-500/40">
          <CardContent className="pt-5">
            <h3 className="font-semibold text-sm mb-1 text-amber-600">Productos vendidos sin costo cargado</h3>
            <p className="text-xs text-muted-foreground mb-3">
              De estos no se puede medir el margen. Cargales el costo en Productos y el reporte los suma solo.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {d.sinCosto.map((r: any, i: number) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-2 truncate max-w-[300px]">{r.name}</td>
                      <td className="py-1.5 pr-2 text-right text-muted-foreground">{r.unidades} u</td>
                      <td className="py-1.5 text-right">{money(r.venta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
